import { CollectionDataType, InsightTag } from '@ecosystem/newron-client-collections/build/shared/types';
import { useState } from 'react';
import { BubbleChartResp, SaleType } from '@models/OpportunityReview';
import { getPeriodicityForMATAndYTD, isPeriodValueYTDOrMAT } from '@utils/PeriodUtils';
import { capitalizeEachWord } from '@utils/StringUtils';
import {
  addPgLoadedTagInPlace,
  getGeneralCollectionInsightTags,
  getPeriodLabel,
} from '@utils/collectionUtils/CollectionUtils';
import { RelativePeriod } from '../../comparison-period-selector/Types';
import { Periodicity } from '../../time-period-selector/PeriodsTypes';
import { OpportunityReviewBubbleChartInsight } from './Model';

const DISPLAY_BY_LABEL: Record<SaleType, string> = {
  [SaleType.VALUE]: 'Revenue',
  [SaleType.VOLUME]: 'Units',
};

export const getBubbleChartInsightInfoTags = async (
  insight: OpportunityReviewBubbleChartInsight,
): Promise<InsightTag[]> => {
  const {
    apiParams: {
      clientId,
      periodType,
      period,
      comparisonPeriod,
      country,
      productType,
      productGroup,
      saleType,
      segmentName,
      appliedFilters,
    },
    retailerType,
    growthType,
    collectionDataType,
    isBubbleChartV2,
    retailerClientName,
  } = insight;

  const effectivePeriod = isBubbleChartV2 ? formatBubbleSliderPeriodV2(periodType, period) : period;
  const baseTimePeriod = {
    label: effectivePeriod,
    value: effectivePeriod,
    lastUpdatedDate: 0,
  };
  const { periodicity: effectivePeriodicity } = getPeriodicityForMATAndYTD(baseTimePeriod, periodType);
  const isBubbleSliderPeriodV2YtdOrMat = isBubbleChartV2 && isPeriodValueYTDOrMAT(effectivePeriod);

  const periodLabel = await getPeriodLabel({
    clientId,
    periodicity: effectivePeriodicity,
    baseTimePeriod,
    comparisonPeriod: comparisonPeriod as RelativePeriod,
    countryIds: country.map(({ id }) => id),
    productType,
    collectionDataType: isBubbleSliderPeriodV2YtdOrMat ? 'live' : collectionDataType,
    productGroups: productGroup,
  });

  const baseTags = getGeneralCollectionInsightTags({
    periodType: effectivePeriodicity,
    comparisonPeriod:
      typeof comparisonPeriod === 'object' && comparisonPeriod !== null
        ? JSON.stringify(comparisonPeriod)
        : String(comparisonPeriod),
    selectedGrowthType: growthType,
    appliedFilters: appliedFilters,
    period: periodLabel,
    retailerClientName: retailerClientName,
  });
  const baseApiParams = {
    clientId: clientId,
    productType: productType,
    periodType: periodType,
    period: baseTimePeriod.value,
    comparisonPeriod: comparisonPeriod as RelativePeriod,
    countries: country,
    productGroup: productGroup,
    appliedFilters: appliedFilters,
  };
  await addPgLoadedTagInPlace(baseTags, baseApiParams, collectionDataType);
  const otherTags = [
    {
      key: 'DisplayBy',
      value: capitalizeEachWord(DISPLAY_BY_LABEL[saleType]),
    },
    {
      key: 'Show performance within',
      value: capitalizeEachWord(segmentName),
    },
    {
      key: 'DatapointSize',
      value: capitalizeEachWord(`${retailerType} revenue`),
    },
  ];

  return [...baseTags, ...otherTags];
};

/**
 * This custom hook returns a callback that is used to re-render the collection bubble chart.
 *
 * Example use case:
 * Using this hook helps to redraw the annotations on the bubble chart, which is destroyed when
 * exporting the chart, by re-rendering the component.
 */
export const useReRenderBubbleChart = () => {
  const [triggerRender, setTriggerRender] = useState<boolean>(false);

  const reRenderBubbleChart = () => {
    setTriggerRender((prev) => !prev);
  };

  return { triggerRender, reRenderBubbleChart };
};

export const getBubbleChartSelectedPeriod = (
  collectionDataType: CollectionDataType,
  periodicity: Periodicity,
  period: string,
  comparisonPeriod: RelativePeriod,
  chartData: BubbleChartResp | undefined,
) => {
  return getBubbleChartPeriodLiveDataOrV3(chartData);
};

const getBubbleChartPeriodLiveDataOrV3 = (chartData: BubbleChartResp | undefined) => {
  // The structure of chartData for both v2 and v3 is such that the last element of either the
  // market or the retailer array represents the latest period.
  const isSalesRecordsFound = chartData?.salesRecordsFound;

  return isSalesRecordsFound ? chartData.content[0].market[chartData.content[0].market.length - 1].period : '';
};

const formatBubbleSliderPeriodV2 = (periodicity: Periodicity, period: string) => {
  if (!isPeriodValueYTDOrMAT(period)) {
    return period;
  }

  return periodicity === Periodicity.Weekly ? `${period} Week` : period;
};
